import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked, OnDestroy } from '@angular/core';
import { ChatService, StartChatSessionRequest, ChatMessageDto } from '../../services/chat.service';
import { Subscription, interval } from 'rxjs';
import { BranchService, Branch } from '../../services/data/branch.service';
import { MarketSummaryService } from '../../services/data/market-summary.service';

interface ChatMessage {
  text: string;
  isUser: boolean;
  timestamp: Date;
  id: string;
  options?: QuickOption[];
  stockDetail?: StockDetailMessage;
}

interface QuickOption {
  text: string;
  action: string;
  data?: any;
}

interface StockDetailMessage {
  name: string;
  price: string | number;
  changeValue: string | number;
  changePercent?: string | number | null;
  volume?: string | number | null;
  high?: string | number | null;
  low?: string | number | null;
  open?: string | number | null;
  previousClose?: string | number | null;
  isPositiveChange: boolean | null;
}

interface UserInfo {
  name: string;
  phone: string;
  email: string;
  language: 'en' | 'ar';
}

interface BankAccount {
  bankName: string;
  bankNameAr: string;
  iban: string;
  accountNumber: string;
  currency: 'EGP' | 'USD';
}

enum ConversationState {
  LANGUAGE_SELECTION = 'language_selection',
  NAME_COLLECTION = 'name_collection',
  PHONE_COLLECTION = 'phone_collection',
  EMAIL_COLLECTION = 'email_collection',
  CONVERSATION_READY = 'conversation_ready',
  MAIN_MENU = 'main_menu',
  SERVICES_MENU = 'services_menu',
  APPLICATION_USAGE = 'application_usage',
  WEBSITE_USAGE = 'website_usage',
  BRANCH_ADDRESSES = 'branch_addresses',
  CUSTOMER_SERVICE = 'customer_service',
  BACKEND_CHAT = 'backend_chat',
  STOCK_SEARCH = 'stock_search',
  CURRENCY_SELECTION = 'currency_selection',
  ACCOUNT_OPENING_FULL_NAME = 'account_opening_full_name',
  ACCOUNT_OPENING_PHONE = 'account_opening_phone',
  ACCOUNT_OPENING_EMAIL = 'account_opening_email',
  ACCOUNT_OPENING_ADDRESS = 'account_opening_address',
  ACCOUNT_OPENING_EXPERIENCE = 'account_opening_experience',
  ACCOUNT_OPENING_OTHER_COMPANY = 'account_opening_other_company'
}

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss']
})
export class ChatbotComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  isOpen = false;
  messages: ChatMessage[] = [];
  currentMessage = '';
  isTyping = false;
  isMinimized = false;
  userHasScrolledUp = false; // Track if user has manually scrolled up
  
  // Backend chat properties
  private messagesSubscription!: Subscription;
  private messagePollingSubscription!: Subscription;
  private sessionStarted = false;
  private lastMessageCount = 0;
  
  // User information and conversation state
  userInfo: UserInfo = {
    name: '',
    phone: '',
    email: '',
    language: 'en'
  };
  
  conversationState: ConversationState = ConversationState.LANGUAGE_SELECTION;
  
  // Stock search properties
  stocks: any[] = [];
  filteredStocks: any[] = [];
  isSearchingStocks = false;

  // Account opening form data
  accountOpeningForm = {
    fullName: '',
    phoneNumber: '',
    email: '',
    address: '',
    hasExperience: false,
    tradesWithOtherCompany: false
  };

  // Stock recommendations form
  stockRecoAccountNumber: string = '';
  private awaitingStockRecoAccount: boolean = false;

  // Bank accounts data extracted from the image
  private bankAccounts: BankAccount[] = [
    // EGP Accounts
    {
      bankName: 'Ahli United - Cairo',
      bankNameAr: 'الاهلي المتحد - القاهرة',
      iban: 'EG830020000100000001100043010',
      accountNumber: '0001-100043-010',
      currency: 'EGP'
    },
    {
      bankName: 'National Bank of Egypt Abbas El Akkad',
      bankNameAr: 'الاهلي المصرى عباس العقاد',
      iban: 'EG340003007330707474593000180',
      accountNumber: '0733070747459300018',
      currency: 'EGP'
    },
    {
      bankName: 'Midbank - Abbas El Akkad',
      bankNameAr: 'ميد بنك - عباس العقاد',
      iban: 'EG690033000700000700133002000',
      accountNumber: '07-001330-02-000',
      currency: 'EGP'
    },
    {
      bankName: 'Arab African International Bank Garden City',
      bankNameAr: 'العربي الافريقي جاردن سيتي',
      iban: 'EG130057001001009317010020202',
      accountNumber: '1009317010020202',
      currency: 'EGP'
    },
    {
      bankName: 'Arab African International Bank - Golf',
      bankNameAr: 'العربي الافريقي - الجولف',
      iban: 'EG130057001001009317010020201',
      accountNumber: '1009317010020201',
      currency: 'EGP'
    },
    {
      bankName: 'Bank Misr - Abbas El Akkad',
      bankNameAr: 'بنك مصر - عباس العقاد',
      iban: 'EG910002046604660001000004267',
      accountNumber: '4660001000004267',
      currency: 'EGP'
    },
    {
      bankName: 'Commercial International Bank - Abbas El Akkad',
      bankNameAr: 'التجاري الدولي - عباس العقاد',
      iban: 'EG490010001400000100011910437',
      accountNumber: '100011910437',
      currency: 'EGP'
    },
    {
      bankName: 'BLOM Bank Shubra Branch',
      bankNameAr: 'بلوم فرع شبرا',
      iban: 'EG330013001900000100000116540',
      accountNumber: '100000116540',
      currency: 'EGP'
    },
    {
      bankName: 'Audi Bank - Mansoura Branch',
      bankNameAr: 'عوده - فرع المنصوره',
      iban: 'EG120019002200000000584590003',
      accountNumber: '000584590003',
      currency: 'EGP'
    },
    {
      bankName: 'First Abu Dhabi Bank - Abbas El Akkad',
      bankNameAr: 'ابو ظبي الوطني - فرع عباس العقاد',
      iban: 'EG220042011900000050950171220',
      accountNumber: '50950171220',
      currency: 'EGP'
    },
    {
      bankName: 'Qatar National Bank Al Ahli - Nasr City Branch',
      bankNameAr: 'قطر الوطني الاهلي - فرع مدينة نصر',
      iban: 'EG920037071208182031154947514',
      accountNumber: '712-20311549475-14',
      currency: 'EGP'
    },
    {
      bankName: 'AlexBank - Cairo',
      bankNameAr: 'الاسكندرية - القاهرة',
      iban: 'EG330005100100000101075691001',
      accountNumber: '101075691001',
      currency: 'EGP'
    },
    {
      bankName: 'Arab Investment Bank - Abd El Khalek Tharwat',
      bankNameAr: 'الاستثمار العربي - عبد الخالق ثروت',
      iban: 'EG340035000600620300391211000',
      accountNumber: '0620-3003912-11000',
      currency: 'EGP'
    },
    {
      bankName: 'SAIB Bank - New Maadi',
      bankNameAr: 'الشركة المصرفيه العربيه الدوليه م الجديدة',
      iban: 'EG150004103000000305010189908',
      accountNumber: '00305010189908',
      currency: 'EGP'
    },
    {
      bankName: 'Cairo Bank - Adly',
      bankNameAr: 'القاهرة عدلي',
      iban: 'EG840040002001924610473010012',
      accountNumber: '1924610473010012',
      currency: 'EGP'
    },
    {
      bankName: 'ABC Bank - El Bustan',
      bankNameAr: 'المؤسسة العربية المصرفيه - م البستان',
      iban: 'EG390023000400401111613610010',
      accountNumber: '0401111613610010',
      currency: 'EGP'
    },
    {
      bankName: 'Faisal Islamic Bank - Cairo',
      bankNameAr: 'فيصل الاسلامي - القاهرة',
      iban: 'EG140059000100000100014197292',
      accountNumber: '100014197292 / 200012449754',
      currency: 'EGP'
    },
    {
      bankName: 'Faisal Islamic Bank - Giza',
      bankNameAr: 'فيصل الاسلامي - الجيزة',
      iban: 'EG500059001500000100014967941',
      accountNumber: '200013181521 / 100014967941',
      currency: 'EGP'
    },
    {
      bankName: 'Suez Canal Bank - Cairo',
      bankNameAr: 'قناة السويس - القاهرة',
      iban: 'EG590017000100130321410100101',
      accountNumber: '130321410100101',
      currency: 'EGP'
    },
    {
      bankName: 'Al Ahli Bank of Kuwait - Cairo',
      bankNameAr: 'الاهلي الكويتي - القاهرة',
      iban: 'EG850018000500000000013152201',
      accountNumber: '13152201',
      currency: 'EGP'
    },
    {
      bankName: 'Abu Dhabi Islamic Bank - Dokki Branch',
      bankNameAr: 'مصرف أبو ظبي الاسلامي فرع الدقى',
      iban: 'EG251010211100100000060576',
      accountNumber: '832026/100000060576',
      currency: 'EGP'
    },
    {
      bankName: 'Export Development Bank of Egypt - New Egypt',
      bankNameAr: 'المصرى لتنمية الصادرات مصر الجديدة',
      iban: 'EG850061002702020011882801014',
      accountNumber: '2020011882801014',
      currency: 'EGP'
    },
    {
      bankName: 'Egyptian Gulf Bank - Giza Branch',
      bankNameAr: 'المصرى الخليجي - فرع الجيزة',
      iban: 'EG470029000600000006600771001',
      accountNumber: '0006-600771-001',
      currency: 'EGP'
    },
    {
      bankName: 'Emirates NBD - Abbas El Akkad Branch',
      bankNameAr: 'الامارات دبي الوطني - فرع عباس العقاد',
      iban: 'EG820014004450000499239922301',
      accountNumber: '499239922301',
      currency: 'EGP'
    },
    {
      bankName: 'Housing and Development Bank - Nasr City Branch',
      bankNameAr: 'التعمير والاسكان - فرع مدينة نصر',
      iban: 'EG280038000900000090000032267',
      accountNumber: '90000032267',
      currency: 'EGP'
    },
    // USD Accounts
    {
      bankName: 'Ahli United',
      bankNameAr: 'Ahli United',
      iban: 'EG070020000100000001100043020',
      accountNumber: '0001-100043-020',
      currency: 'USD'
    },
    {
      bankName: 'NBE',
      bankNameAr: 'NBE',
      iban: 'EG100003007330607474593000180',
      accountNumber: '0733060747459300018',
      currency: 'USD'
    },
    {
      bankName: 'MIDBANK',
      bankNameAr: 'MIDBANK',
      iban: 'EG060033000700000700133001000',
      accountNumber: '07-001330-01-000',
      currency: 'USD'
    },
    {
      bankName: 'AAIB-H.O',
      bankNameAr: 'AAIB-H.O',
      iban: 'EG410057001001009317010020102',
      accountNumber: '1009317010020102',
      currency: 'USD'
    },
    {
      bankName: 'AAIB-Golf',
      bankNameAr: 'AAIB-Golf',
      iban: 'EG410057001001009317010020101',
      accountNumber: '1009317010020101',
      currency: 'USD'
    },
    {
      bankName: 'Misr',
      bankNameAr: 'Misr',
      iban: 'EG070002046604660120000001151',
      accountNumber: '4660120000001151',
      currency: 'USD'
    },
    {
      bankName: 'CIB',
      bankNameAr: 'CIB',
      iban: 'EG460010001400000100009825767',
      accountNumber: '100009825767',
      currency: 'USD'
    },
    {
      bankName: 'AUDI',
      bankNameAr: 'AUDI',
      iban: 'EG280019002200000000584590006',
      accountNumber: '000584590006',
      currency: 'USD'
    },
    {
      bankName: 'First AboDhabi Bank',
      bankNameAr: 'First AboDhabi Bank',
      iban: 'EG090037071208402031167771496',
      accountNumber: '712-20311677714-96',
      currency: 'USD'
    },
    {
      bankName: 'SAIB',
      bankNameAr: 'SAIB',
      iban: 'EG760035000600601300391211000',
      accountNumber: '0601-3003912-11000',
      currency: 'USD'
    },
    {
      bankName: 'CAIRO',
      bankNameAr: 'CAIRO',
      iban: 'EG070004103000000306010061784',
      accountNumber: '00306010061784',
      currency: 'USD'
    },
    {
      bankName: 'ABC',
      bankNameAr: 'ABC',
      iban: 'EG850040002001924610472020012',
      accountNumber: '1924610472020012',
      currency: 'USD'
    },
    {
      bankName: 'NBK',
      bankNameAr: 'NBK',
      iban: 'EG470023000400402111613610010',
      accountNumber: '0402111613610010',
      currency: 'USD'
    },
    {
      bankName: 'FISAL - CAIRO',
      bankNameAr: 'FISAL - CAIRO',
      iban: 'EG840059000100000100014197293',
      accountNumber: '100014197293 / 200012449755',
      currency: 'USD'
    },
    {
      bankName: 'FISAL - GIZA',
      bankNameAr: 'FISAL - GIZA',
      iban: 'EG880059001500000100015418210',
      accountNumber: '200013181522 / 100014967942',
      currency: 'USD'
    },
    {
      bankName: 'ABK',
      bankNameAr: 'ABK',
      iban: 'EG790018000500000000013152212',
      accountNumber: '13152212',
      currency: 'USD'
    },
    {
      bankName: 'EDBE',
      bankNameAr: 'EDBE',
      iban: 'EG360061002702020011882801023',
      accountNumber: '2020011882801020',
      currency: 'USD'
    },
    {
      bankName: 'EGY BANK',
      bankNameAr: 'EGY BANK',
      iban: 'EG200029000600000006600771002',
      accountNumber: '0006-600771-002',
      currency: 'USD'
    },
    {
      bankName: 'NBD',
      bankNameAr: 'NBD',
      iban: 'EG670014004500001029239922302',
      accountNumber: '1029239922302',
      currency: 'USD'
    }
  ];
  
  // Language-specific messages
  messages_en = {
    welcome: "👋 Hello! Welcome to our website. I'm your AI assistant.",
    languageSelection: "Please choose your preferred language:",
    nameRequest: "Great! Now, could you please tell me your name?",
    phoneRequest: "Nice to meet you, {name}! Could you please provide your phone number?",
    emailRequest: "Thank you! And what's your email address?",
    infoComplete: "Perfect! Thank you for providing your information, {name}. How can I help you today?",
    invalidEmail: "Please provide a valid email address.",
    invalidPhone: "Please provide a valid phone number.",
    helpMessage: "I'm here to help! You can ask me about our services, pricing, contact information, or anything else you'd like to know.",
    mainMenu: "Please select from the main menu:",
    aboutCompany: "We are a fully integrated investment holding company with a diverse portfolio, targeting compelling business opportunities to create superior shareholder value over the long term, not only in Egypt and the MENA region but globally. Tapping into our deep local knowledge, proven track record, and extensive business networks to identify clear winners and unlock their inherent value, our capital is at work in over 50 portfolio companies that employ c. 6,000 people.",
    workingHours: "Working Hours: 9 AM to 4 PM",
    accountOpeningRequest: "Account Opening Request: You can fill out our Google Form to open an account. Would you like me to provide the link?",
    accountNumbers: "Account Numbers: We have accounts with multiple banks including CIB, United National Bank, National Bank of Egypt, Bank of Cairo, and Bank Misr. Please contact our customer service for specific account numbers.",
    accountNumbersDetailed: "Here are our bank account details:",
    selectCurrencyMessage: "Please select which currency accounts you would like to view:",
    egpAccountsTitle: "Egyptian Pound (EGP) Accounts:",
    usdAccountsTitle: "US Dollar (USD) Accounts:",
    bankAccountFormat: "Bank: {bankName}\nIBAN: {iban}\nAccount Number: {accountNumber}",
    servicesMenu: "Our Services include:",
    accountManager: "Account Manager: Our dedicated account managers provide personalized investment advice and portfolio management services.",
    mobileApp: "Mobile Application: Our mobile app provides real-time market data, trading capabilities, and portfolio tracking.",
    eTrade: "E-Trade: Our online trading platform offers advanced charting tools, real-time quotes, and seamless trading experience.",
    margin: "Margin: We offer competitive margin trading services with flexible terms and competitive rates.",
    competitiveCommissions: "Competitive Commissions: We offer some of the most competitive commission rates in the market.",
    financialGroup: "Financial and Technical Recommendations Group: Our expert team provides comprehensive market analysis and investment recommendations.",
    applicationUsage: "How to Use the Application:",
    appExplanation: "Application Explanation Link: https://www.youtube.com/watch?v=rt4SGXYLTpo",
    iosDownload: "iOS Application Download: Available on App Store https://apps.apple.com/eg/app/pioneers-go/id6739808913",
    androidDownload: "Android Application Download: Available on Google Play Store https://play.google.com/store/apps/details?id=com.fitoman.piogo&hl=en",
    websiteUsage: "How to Use the Website",
    websiteExplanation: "Website Explanation: Our website provides comprehensive market information and trading tools.",
    websiteLink: "Website Link: https://www.pioneers-securities.com/web/",
    branchAddresses: "Branch Addresses: We have multiple branches across Egypt. Which location would you like information about?",
    mainCenter: "Main Center: Located in Cairo with full trading services and customer support.",
    heliopolis: "Heliopolis (مصر الجديدة): Located in Heliopolis district with comprehensive trading services.",
    elBatrawy: "El Batrawy (البطراوي): Located in El Batrawy area with full trading capabilities.",
    elTagamoa: "El Tagamoa (التجمع): Located in El Tagamoa district with complete trading services.",
    dokki: "Dokki (الدقي): Located in Dokki area with comprehensive trading facilities.",
    sheikhZayed: "Sheikh Zayed (الشيخ زايد): Located in Sheikh Zayed with full trading services.",
    stanleyAlexandria: "Stanley - Alexandria (استانلي اسكندريه): Located in Alexandria with complete trading services.",
    smouhaAlexandria: "Smouha - Alexandria (سموحه - اسكندريه): Located in Smouha, Alexandria with full trading capabilities.",
    mansoura: "Mansoura (المنصورة): Located in Mansoura with comprehensive trading services.",
    damietta: "Damietta (دمياط): Located in Damietta with full trading facilities.",
    menoufia: "Menoufia (المنوفيه): Located in Menoufia with complete trading services.",
    elManzala: "El Manzala (المنزله): Located in El Manzala with comprehensive trading capabilities.",
    assiut: "Assiut (اسيوط): Located in Assiut with full trading services.",
    // Backend chat messages
    sessionStarted: "Session started successfully! You can now chat with our support team.",
    sessionError: "Sorry, there was an error starting your session. Please try again.",
    messageError: "Sorry, there was an error sending your message. Please try again.",
    connecting: "Connecting to support system...",
    waitingForAgent: "Please wait while we connect you to an agent...",
    chatWithCustomerService: "Chat with Customer Service: Connect directly with our live support team for personalized assistance.",
    // Stock prices messages
    stockPrices: "Stock Prices: Search for any stock to get real-time prices and information.",
    stockSearchPrompt: "Please enter the name of the stock you want to search for:",
    stockNotFound: "Sorry, we couldn't find that stock. Please try searching with a different name.",
    stockInfo: "Stock Information:",
    stockPrice: "Current Price:",
    stockChange: "Change:",
    stockVolume: "Volume:",
    stockMarketCap: "Market Cap:",
    stockHigh: "High:",
    stockLow: "Low:",
    stockOpen: "Open:",
    stockPreviousClose: "Previous Close:",
    // Account opening messages
    accountOpeningTitle: "Account Opening Request",
    accountOpeningFullName: "Please enter your full name:",
    accountOpeningPhone: "Please enter your phone number:",
    accountOpeningEmail: "Please enter your email address:",
    accountOpeningAddress: "Please enter your address (to find the nearest branch):",
    accountOpeningExperience: "Do you have previous experience in the stock market?",
    accountOpeningOtherCompany: "Do you trade with another company?",
    accountOpeningSuccess: "Thank you! Your account opening request has been submitted successfully. We will contact you soon.",
    accountOpeningError: "Sorry, there was an error submitting your request. Please try again.",
    yes: "Yes",
    no: "No",
    // Stock recommendations
    stockRecoAskAccount: "Please enter your trading account number:",
    stockRecoSuccess: "Your stock recommendations request has been submitted. Our team will contact you soon.",
    stockRecoError: "There was an error submitting your request. Please try again."
  };
  
  messages_ar = {
    welcome: "👋 أهلاً وسهلاً! مرحباً بك في موقعنا. أنا مساعدك الذكي.",
    languageSelection: "يرجى اختيار اللغة المفضلة لديك:",
    nameRequest: "رائع! الآن، هل يمكنك إخباري باسمك من فضلك؟",
    phoneRequest: "تشرفنا بلقائك، {name}! هل يمكنك تزويدي برقم هاتفك؟",
    emailRequest: "شكراً لك! وما هو عنوان بريدك الإلكتروني؟",
    infoComplete: "ممتاز! شكراً لك على تقديم معلوماتك، {name}. كيف يمكنني مساعدتك اليوم؟",
    invalidEmail: "يرجى تقديم عنوان بريد إلكتروني صحيح.",
    invalidPhone: "يرجى تقديم رقم هاتف صحيح.",
    helpMessage: "أنا هنا لمساعدتك! يمكنك سؤالي عن خدماتنا، الأسعار، معلومات الاتصال، أو أي شيء آخر تود معرفته.",
    mainMenu: "يرجى الاختيار من القائمة الرئيسية:",
    aboutCompany: "نحن شركة قابضة متكاملة بالكامل بمحفظة متنوعة، تستهدف فرص الأعمال الجذابة لخلق قيمة للمساهمين على المدى الطويل، ليس فقط في مصر ومنطقة الشرق الأوسط وشمال إفريقيا ولكن على مستوى العالم. نستفيد من معرفتنا المحلية العميقة، وسجلنا الحافل، وشبكاتنا التجارية الواسعة لتحديد الفائزين الواضحين وكشف قيمتهم الجوهرية، حيث يعمل رأس مالنا في أكثر من 50 شركة محفظة توظف حوالي 6000 شخص.",
    workingHours: "مواعيد العمل: 9 صباحا حتى 4 مساءا",
    accountOpeningRequest: "طلب فتح حساب: يمكنك ملء نموذج جوجل لفتح حساب. هل تريد مني تزويدك بالرابط؟",
    accountNumbers: "ارقام الحسابات: لدينا حسابات في عدة بنوك تشمل CIB والبنك الأهلي المتحد والبنك الأهلي المصري وبنك القاهرة وبنك مصر. يرجى الاتصال بخدمة العملاء للحصول على أرقام الحسابات المحددة.",
    accountNumbersDetailed: "إليك تفاصيل حساباتنا البنكية:",
    selectCurrencyMessage: "يرجى اختيار حسابات العملة التي تريد عرضها:",
    egpAccountsTitle: "حسابات الجنيه المصري (EGP):",
    usdAccountsTitle: "حسابات الدولار الأمريكي (USD):",
    bankAccountFormat: "البنك: {bankName}\nIBAN: {iban}\nرقم الحساب: {accountNumber}",
    servicesMenu: "خدماتنا تشمل:",
    accountManager: "مدير حساب: مدراء الحسابات المخصصون لدينا يقدمون نصائح استثمارية مخصصة وخدمات إدارة المحافظ.",
    mobileApp: "تطبيق الموبيل: تطبيقنا المحمول يوفر بيانات السوق في الوقت الفعلي وقدرات التداول وتتبع المحفظة.",
    eTrade: "التداول الإلكتروني: منصة التداول عبر الإنترنت لدينا تقدم أدوات رسم بياني متقدمة وعروض أسعار فورية وتجربة تداول سلسة.",
    margin: "مارجن: نقدم خدمات تداول الهامش التنافسية بشروط مرنة وأسعار تنافسية.",
    competitiveCommissions: "عمولات تنافسيه: نقدم بعض من أكثر معدلات العمولات تنافسية في السوق.",
    financialGroup: "جروب توصيات مالية وفنيه: فريق الخبراء لدينا يقدم تحليل شامل للسوق وتوصيات استثمارية.",
    applicationUsage: "طريقه استخدام التطبيق:",
    appExplanation: "لينك شرح التطبيق: https://www.youtube.com/watch?v=rt4SGXYLTpo",
    iosDownload: "تنزيل تطبيق iOS: متاح على App Store https://apps.apple.com/eg/app/pioneers-go/id6739808913",
    androidDownload: "تنزيل تطبيق Android: متاح على Google Play Store https://play.google.com/store/apps/details?id=com.fitoman.piogo&hl=en",
    websiteUsage: "طريقه استخدام الويبسايت",
    websiteExplanation: "شرح الويبسايت: موقعنا يوفر معلومات شاملة عن السوق وأدوات التداول.",
    websiteLink: "رابط الويبسايت: https://www.pioneers-securities.com/web/",
    branchAddresses: "عناوين الفروع: لدينا عدة فروع في جميع أنحاء مصر. أي موقع تريد معلومات عنه؟",
    mainCenter: "المركز الرئيسي: يقع في القاهرة مع خدمات تداول كاملة ودعم العملاء.",
    heliopolis: "مصر الجديدة: يقع في حي مصر الجديدة مع خدمات تداول شاملة.",
    elBatrawy: "البطراوي: يقع في منطقة البطراوي مع قدرات تداول كاملة.",
    elTagamoa: "التجمع: يقع في حي التجمع مع خدمات تداول كاملة.",
    dokki: "الدقي: يقع في منطقة الدقي مع مرافق تداول شاملة.",
    sheikhZayed: "الشيخ زايد: يقع في الشيخ زايد مع خدمات تداول كاملة.",
    stanleyAlexandria: "استانلي اسكندريه: يقع في الإسكندرية مع خدمات تداول كاملة.",
    smouhaAlexandria: "سموحه - اسكندريه: يقع في سموحه، الإسكندرية مع قدرات تداول كاملة.",
    mansoura: "المنصورة: يقع في المنصورة مع خدمات تداول شاملة.",
    damietta: "دمياط: يقع في دمياط مع مرافق تداول كاملة.",
    menoufia: "المنوفيه: يقع في المنوفية مع خدمات تداول كاملة.",
    elManzala: "المنزله: يقع في المنزلة مع قدرات تداول شاملة.",
    assiut: "اسيوط: يقع في أسيوط مع خدمات تداول كاملة.",
    // Backend chat messages
    sessionStarted: "تم بدء الجلسة بنجاح! يمكنك الآن التحدث مع فريق الدعم.",
    sessionError: "عذراً، حدث خطأ في بدء جلستك. يرجى المحاولة مرة أخرى.",
    messageError: "عذراً، حدث خطأ في إرسال رسالتك. يرجى المحاولة مرة أخرى.",
    connecting: "جاري الاتصال بنظام الدعم...",
    waitingForAgent: "يرجى الانتظار بينما نقوم بربطك بوكيل الدعم...",
    chatWithCustomerService: "التحدث مع خدمة العملاء: تواصل مباشرة مع فريق الدعم المباشر للحصول على مساعدة مخصصة.",
    // Stock prices messages
    stockPrices: "أسعار الأسهم: ابحث عن أي سهم للحصول على أسعار فورية ومعلومات.",
    stockSearchPrompt: "الرجاء إدخال اسم السهم الذي تريد البحث عنه:",
    stockNotFound: "عذراً، لم نتمكن من العثور على هذا السهم. يرجى المحاولة مرة أخرى باسم مختلف.",
    stockInfo: "معلومات السهم:",
    stockPrice: "السعر الحالي:",
    stockChange: "التغير:",
    stockVolume: "الحجم:",
    stockMarketCap: "القيمة السوقية:",
    stockHigh: "الأعلى:",
    stockLow: "الأدنى:",
    stockOpen: "السعر الأول:",
    stockPreviousClose: "السعر السابق:",
    // Account opening messages
    accountOpeningTitle: "طلب فتح حساب",
    accountOpeningFullName: "يرجى إدخال اسمك بالكامل:",
    accountOpeningPhone: "يرجى إدخال رقم هاتفك:",
    accountOpeningEmail: "يرجى إدخال بريدك الإلكتروني:",
    accountOpeningAddress: "يرجى إدخال عنوانك (لمعرفة أقرب فرع):",
    accountOpeningExperience: "هل لديك خبرة سابقة في البورصة؟",
    accountOpeningOtherCompany: "هل تتداول مع شركة أخرى؟",
    accountOpeningSuccess: "شكراً لك! تم إرسال طلب فتح الحساب بنجاح. سنتواصل معك قريباً.",
    accountOpeningError: "عذراً، حدث خطأ في إرسال طلبك. يرجى المحاولة مرة أخرى.",
    yes: "نعم",
    no: "لا",
    // Stock recommendations
    stockRecoAskAccount: "من فضلك أدخل رقم حساب التداول:",
    stockRecoSuccess: "تم إرسال طلب توصيات الأسهم وسيقوم فريقنا بالتواصل معك قريباً.",
    stockRecoError: "حدث خطأ أثناء إرسال الطلب. يرجى المحاولة مرة أخرى."
  };

  constructor(private chatService: ChatService, private branchService: BranchService, private marketSummaryService: MarketSummaryService) { }

  ngOnInit(): void {
    // Start with language selection
    this.addBotMessage(this.messages_en.welcome);
    setTimeout(() => {
      this.addBotMessage(this.messages_en.languageSelection);
      this.showLanguageButtons();
    }, 1000);
  }

  ngAfterViewChecked(): void {
    // Removed automatic scroll to prevent interference with user scrolling
    // Scroll will only happen when new messages are added and user is at bottom
  }

  ngOnDestroy(): void {
    if (this.messagesSubscription) {
      this.messagesSubscription.unsubscribe();
    }
    if (this.messagePollingSubscription) {
      this.messagePollingSubscription.unsubscribe();
    }
    if (this.sessionStarted) {
      this.chatService.endSession();
    }
  }

  toggleChat(): void {
    this.isOpen = !this.isOpen;
  }

  minimizeChat(): void {
    this.isMinimized = !this.isMinimized;
  }

  closeChat(): void {
    this.isOpen = false;
    this.isMinimized = false;
  }

  sendMessage(): void {
    if (this.currentMessage.trim()) {
      this.addUserMessage(this.currentMessage);
      const userMessage = this.currentMessage;
      this.currentMessage = '';
      
      // Process message based on conversation state
      this.processUserInput(userMessage);
    }
  }

  processUserInput(message: string): void {
    this.isTyping = true;
    
    setTimeout(async () => {
      // Intercept stock recommendations account capture regardless of state
      if (this.awaitingStockRecoAccount) {
        this.handleStockRecommendationsAccount(message);
        this.isTyping = false;
        return;
      }
      switch (this.conversationState) {
        case ConversationState.NAME_COLLECTION:
          this.handleNameInput(message);
          break;
        case ConversationState.PHONE_COLLECTION:
          this.handlePhoneInput(message);
          break;
        case ConversationState.EMAIL_COLLECTION:
          this.handleEmailInput(message);
          break;
        case ConversationState.CONVERSATION_READY:
          this.handleGeneralConversation(message);
          break;
        case ConversationState.MAIN_MENU:
          this.handleMainMenuSelection(message);
          break;
        case ConversationState.SERVICES_MENU:
          this.handleServicesMenuSelection(message);
          break;
        case ConversationState.APPLICATION_USAGE:
          this.handleApplicationUsageSelection(message);
          break;
        case ConversationState.WEBSITE_USAGE:
          this.handleWebsiteUsageSelection(message);
          break;
        case ConversationState.BRANCH_ADDRESSES:
          this.handleBranchAddressSelection(message);
          break;
        case ConversationState.CUSTOMER_SERVICE:
          this.handleCustomerServiceSelection(message);
          break;
        case ConversationState.BACKEND_CHAT:
          await this.handleBackendConversation(message);
          break;
        case ConversationState.STOCK_SEARCH:
          this.handleStockSearch(message);
          break;
        case ConversationState.CURRENCY_SELECTION:
          this.handleCurrencySelection(message);
          break;
        case ConversationState.ACCOUNT_OPENING_FULL_NAME:
          this.handleAccountOpeningFullName(message);
          break;
        case ConversationState.ACCOUNT_OPENING_PHONE:
          this.handleAccountOpeningPhone(message);
          break;
        case ConversationState.ACCOUNT_OPENING_EMAIL:
          this.handleAccountOpeningEmail(message);
          break;
        case ConversationState.ACCOUNT_OPENING_ADDRESS:
          this.handleAccountOpeningAddress(message);
          break;
        case ConversationState.ACCOUNT_OPENING_EXPERIENCE:
          this.handleAccountOpeningExperience(message);
          break;
        case ConversationState.ACCOUNT_OPENING_OTHER_COMPANY:
          this.handleAccountOpeningOtherCompany(message);
          break;
      }
      this.isTyping = false;
    }, 1000 + Math.random() * 1000);
  }

  selectLanguage(language: 'en' | 'ar'): void {
    this.userInfo.language = language;
    this.addUserMessage(language === 'en' ? 'English' : 'العربية');
    
    this.isTyping = true;
    setTimeout(() => {
      const messages = language === 'en' ? this.messages_en : this.messages_ar;
      this.addBotMessage(messages.nameRequest);
      this.conversationState = ConversationState.NAME_COLLECTION;
      this.isTyping = false;
    }, 800);
  }

  handleNameInput(name: string): void {
    this.userInfo.name = name.trim();
    const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
    const phoneMessage = messages.phoneRequest.replace('{name}', this.userInfo.name);
    this.addBotMessage(phoneMessage);
    this.conversationState = ConversationState.PHONE_COLLECTION;
  }

  handlePhoneInput(phone: string): void {
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{8,}$/;
    if (phoneRegex.test(phone.trim())) {
      this.userInfo.phone = phone.trim();
      const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
      this.addBotMessage(messages.emailRequest);
      this.conversationState = ConversationState.EMAIL_COLLECTION;
    } else {
      const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
      this.addBotMessage(messages.invalidPhone);
    }
  }

  handleEmailInput(email: string): void {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(email.trim())) {
      this.userInfo.email = email.trim();
      const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
      const completeMessage = messages.infoComplete.replace('{name}', this.userInfo.name);
      this.addBotMessage(completeMessage);
      this.conversationState = ConversationState.CONVERSATION_READY;
      
      // Show main menu options
      this.showMainMenuOptions();
      
      // Log user info (you can send this to your backend)
      console.log('User Information Collected:', this.userInfo);
    } else {
      const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
      this.addBotMessage(messages.invalidEmail);
    }
  }

  showMainMenuOptions(): void {
    const isArabic = this.userInfo.language === 'ar';
    const options: QuickOption[] = [
      { text: isArabic ? 'نبذه عن الشركة' : 'About the Company', action: 'about_company' },
      { text: isArabic ? 'خدماتنا' : 'Our Services', action: 'services' },
      { text: isArabic ? 'طريقه استخدام التطبيق' : 'How to Use the Application', action: 'application_usage' },
      { text: isArabic ? 'طريقه استخدام الويبسايت' : 'How to Use the Website', action: 'website_usage' },
      { text: isArabic ? 'عناوين الفروع' : 'Branch Addresses', action: 'branch_addresses' },
      { text: isArabic ? 'خدمة العملاء' : 'Customer Service', action: 'customer_service' },
      { text: isArabic ? 'أسعار الأسهم' : 'Stock Prices', action: 'stock_prices' }
    ];

    const messages = isArabic ? this.messages_ar : this.messages_en;
    this.addBotMessageWithOptions(messages.mainMenu, options);
    this.conversationState = ConversationState.MAIN_MENU;
  }

  handleMainMenuSelection(selection: string): void {
    const isArabic = this.userInfo.language === 'ar';
    
    if (selection.includes('about') || selection.includes('نبذه') || selection.includes('شركة')) {
      const messages = isArabic ? this.messages_ar : this.messages_en;
      this.addBotMessage(messages.aboutCompany);
      this.showReturnAndCustomerServiceOptions();
    } else if (selection.includes('service') || selection.includes('خدمات')) {
      this.showServicesMenu();
    } else if (selection.includes('application') || selection.includes('تطبيق')) {
      this.showApplicationUsageOptions();
    } else if (selection.includes('website') || selection.includes('ويبسايت') || selection.includes('الويبسايت')) {
      this.showWebsiteUsageOptions();
    } else if (selection.includes('branch') || selection.includes('فروع') || selection.includes('عناوين')) {
      this.showBranchAddressOptions();
    } else if (selection.includes('customer') || selection.includes('عملاء') || selection.includes('خدمة')) {
      this.showCustomerServiceOptions();
    } else if (selection.includes('stock') || selection.includes('أسعار') || selection.includes('أسهم')) {
      this.showStockPricesOptions();
    } else {
      this.showMainMenuOptions();
    }
  }

  showStockPricesOptions(): void {
    const isArabic = this.userInfo.language === 'ar';
    const messages = isArabic ? this.messages_ar : this.messages_en;
    
    // Load stocks data if not already loaded
    if (this.stocks.length === 0) {
      this.marketSummaryService.getStocksGridData().subscribe((data: any) => {
        this.stocks = data;
        this.showStockSearchPrompt();
      });
    } else {
      this.showStockSearchPrompt();
    }
  }

  showStockSearchPrompt(): void {
    const isArabic = this.userInfo.language === 'ar';
    const messages = isArabic ? this.messages_ar : this.messages_en;
    
    this.addBotMessage(messages.stockSearchPrompt);
    this.conversationState = ConversationState.STOCK_SEARCH;
    this.isSearchingStocks = true;
    // Force scroll to bottom for stock prices prompt
    setTimeout(() => this.scrollToBottom(), 150);
  }

  handleStockSearch(searchTerm: string): void {
    const isArabic = this.userInfo.language === 'ar';
    const messages = isArabic ? this.messages_ar : this.messages_en;
    
    // Filter stocks based on search term
    const filteredStocks = this.stocks.filter(stock => 
      stock.englishName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      stock.arabicName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    if (filteredStocks.length === 0) {
      this.addBotMessage(messages.stockNotFound);
      this.showStockSearchPrompt();
      return;
    }
    
    // If multiple stocks found, show options
    if (filteredStocks.length > 1) {
      const options: QuickOption[] = filteredStocks.slice(0, 5).map(stock => ({
        text: `${stock.englishName} (${stock.arabicName || ''})`,
        action: 'select_stock',
        data: stock
      }));
      
      const promptMessage = isArabic ? 
        `وجدنا ${filteredStocks.length} سهم. اختر السهم المطلوب:` :
        `Found ${filteredStocks.length} stocks. Please select the stock:`;
      
      this.addBotMessageWithOptions(promptMessage, options);
      this.conversationState = ConversationState.STOCK_SEARCH;
      // Ensure we scroll to the latest options list
      setTimeout(() => this.scrollToBottom(), 150);
    } else {
      // Single stock found, show details
      this.displayStockInfo(filteredStocks[0]);
    }
  }

  displayStockInfo(stock: any): void {
    const isArabic = this.userInfo.language === 'ar';
    const messages = isArabic ? this.messages_ar : this.messages_en;
    
    // Get stock details
    this.marketSummaryService.getStockDetails(stock.isin).subscribe((details: any) => {
      const displayName = isArabic
        ? (stock.arabicName || stock.englishName || '')
        : (stock.englishName || stock.arabicName || '');

      const lastPrice = (details?.lastPrice ?? details?.currentPrice) ?? 'N/A';
      const close = (details?.close ?? details?.previousClose) ?? null;
      const change = (details?.change != null) ? details.change : (typeof lastPrice === 'number' && close != null ? (lastPrice - close) : null);
      const changePercent = (typeof lastPrice === 'number' && typeof close === 'number' && close !== 0)
        ? (((lastPrice - close) / close) * 100).toFixed(2)
        : (details?.changePercent ?? null);

      const stockDetail: StockDetailMessage = {
        name: displayName,
        price: lastPrice,
        changeValue: change ?? 'N/A',
        changePercent: changePercent,
        volume: details?.volume ?? 'N/A',
        high: (details?.highBidPrice ?? details?.high) ?? 'N/A',
        low: (details?.lowAskPrice ?? details?.low) ?? 'N/A',
        open: details?.open ?? 'N/A',
        previousClose: (close ?? 'N/A'),
        isPositiveChange: (typeof change === 'number') ? (change > 0) : null
      };

      const message: ChatMessage = {
        text: '',
        isUser: false,
        timestamp: new Date(),
        id: this.generateId(),
        stockDetail
      };
      this.messages.push(message);
      setTimeout(() => this.scrollToBottom(), 100);
      
      // Show return and customer service options
      this.showReturnAndCustomerServiceOptions();
    }, (error) => {
      this.addBotMessage(isArabic ? 'عذراً، حدث خطأ في جلب معلومات السهم.' : 'Sorry, there was an error fetching stock information.');
      this.showReturnAndCustomerServiceOptions();
    });
  }

  showReturnAndCustomerServiceOptions(): void {
    const isArabic = this.userInfo.language === 'ar';
    const options: QuickOption[] = [
      { text: isArabic ? 'العودة للقائمة السابقة' : 'Return to Previous Menu', action: 'return_to_previous' },
      { text: isArabic ? 'التحدث مع خدمة العملاء' : 'Contact Customer Service', action: 'contact_customer_service' }
    ];
    
    this.addBotMessageWithOptions('', options);
  }

  showServicesMenu(): void {
    const isArabic = this.userInfo.language === 'ar';
    const options: QuickOption[] = [
      { text: isArabic ? 'مدير حساب' : 'Account Manager', action: 'account_manager' },
      { text: isArabic ? 'تطبيق الموبيل' : 'Mobile Application', action: 'mobile_app' },
      { text: isArabic ? 'e- tread' : 'E-Trade', action: 'e_trade' },
      { text: isArabic ? 'مارجن' : 'Margin', action: 'margin' },
      { text: isArabic ? 'عمولات تنافسيه' : 'Competitive Commissions', action: 'competitive_commissions' },
      { text: isArabic ? 'جروب توصيات مالية وفنيه' : 'Financial and Technical Recommendations Group', action: 'financial_group' },
      { text: isArabic ? 'العودة للقائمة الرئيسية' : 'Go Back to Main Menu', action: 'go_back_to_main' }
    ];

    const messages = isArabic ? this.messages_ar : this.messages_en;
    this.addBotMessageWithOptions(messages.servicesMenu, options);
    this.conversationState = ConversationState.SERVICES_MENU;
  }

  handleServicesMenuSelection(selection: string): void {
    const isArabic = this.userInfo.language === 'ar';
    const messages = isArabic ? this.messages_ar : this.messages_en;
    
    if (selection.includes('stock') || selection.includes('توصيات')) {
      this.startStockRecommendationsFlow();
    } else if (selection.includes('account') || selection.includes('مدير')) {
      this.addBotMessage(messages.accountManager);
      this.showReturnAndCustomerServiceOptions();
    } else if (selection.includes('mobile') || selection.includes('موبيل')) {
      this.addBotMessage(messages.mobileApp);
      this.showReturnAndCustomerServiceOptions();
    } else if (selection.includes('trade') || selection.includes('tread')) {
      this.addBotMessage(messages.eTrade);
      this.showReturnAndCustomerServiceOptions();
    } else if (selection.includes('margin') || selection.includes('مارجن')) {
      this.addBotMessage(messages.margin);
      this.showReturnAndCustomerServiceOptions();
    } else if (selection.includes('commission') || selection.includes('عمولات')) {
      this.addBotMessage(messages.competitiveCommissions);
      this.showReturnAndCustomerServiceOptions();
    } else if (selection.includes('financial') || selection.includes('توصيات')) {
      // Reuse this option to start Stock Recommendations ticket flow
      this.startStockRecommendationsFlow();
      return;
    } else if (selection.includes('go back') || selection.includes('العودة') || selection.includes('main')) {
      this.showMainMenuOptions();
    } else {
      this.showServicesMenu();
    }
  }

  // Stock recommendations flow
  startStockRecommendationsFlow(): void {
    const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
    this.awaitingStockRecoAccount = true;
    this.stockRecoAccountNumber = '';
    this.addBotMessage(messages.stockRecoAskAccount);
  }

  private async handleStockRecommendationsAccount(accountInput: string): Promise<void> {
    const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
    const accountNumber = accountInput.trim();
    if (!accountNumber) {
      this.addBotMessage(messages.stockRecoAskAccount);
      return;
    }
    this.awaitingStockRecoAccount = false;
    this.stockRecoAccountNumber = accountNumber;
    try {
      const resp = await this.chatService.createStockRecommendationsTicket({
        accountNumber: this.stockRecoAccountNumber,
        language: this.userInfo.language
      });
      if (resp.success) {
        this.addBotMessage(messages.stockRecoSuccess);
      } else {
        this.addBotMessage(messages.stockRecoError);
      }
    } catch (e) {
      this.addBotMessage(messages.stockRecoError);
    }
    this.showReturnAndCustomerServiceOptions();
  }

  showApplicationUsageOptions(): void {
    const isArabic = this.userInfo.language === 'ar';
    const options: QuickOption[] = [
      { text: isArabic ? 'لينك شرح التطبيق' : 'Link to Application Explanation', action: 'app_explanation' },
      { text: isArabic ? 'ios تنزيل الابلكيشن link' : 'iOS Application Download Link', action: 'ios_download' },
      { text: isArabic ? 'link تزيل الأبلكيشن android' : 'Android Application Download Link', action: 'android_download' },
      { text: isArabic ? 'العودة للقائمة الرئيسية' : 'Go Back to Main Menu', action: 'go_back_to_main' }
    ];

    const messages = isArabic ? this.messages_ar : this.messages_en;
    this.addBotMessageWithOptions(messages.applicationUsage, options);
    this.conversationState = ConversationState.APPLICATION_USAGE;
  }

  handleApplicationUsageSelection(selection: string): void {
    const isArabic = this.userInfo.language === 'ar';
    const messages = isArabic ? this.messages_ar : this.messages_en;
    
    if (selection.includes('explanation') || selection.includes('شرح')) {
      this.handleLinkAction('appExplanation');
      this.showReturnAndCustomerServiceOptions();
    } else if (selection.includes('ios') || selection.includes('iOS')) {
      this.handleLinkAction('iosDownload');
      this.showReturnAndCustomerServiceOptions();
    } else if (selection.includes('android')) {
      this.handleLinkAction('androidDownload');
      this.showReturnAndCustomerServiceOptions();
    } else if (selection.includes('go back') || selection.includes('العودة') || selection.includes('main')) {
      this.showMainMenuOptions();
    } else {
      this.showApplicationUsageOptions();
    }
  }

  showWebsiteUsageOptions(): void {
    const isArabic = this.userInfo.language === 'ar';
    const options: QuickOption[] = [
      { text: isArabic ? 'لينك شرح الويسايت' : 'Link to Website Explanation', action: 'website_explanation' },      { text: isArabic ? 'العودة للقائمة الرئيسية' : 'Go Back to Main Menu', action: 'go_back_to_main' }
    ];

    const messages = isArabic ? this.messages_ar : this.messages_en;
    this.addBotMessageWithOptions(messages.websiteUsage, options);
    this.conversationState = ConversationState.WEBSITE_USAGE;
  }

  handleWebsiteUsageSelection(selection: string): void {
    const isArabic = this.userInfo.language === 'ar';
    const messages = isArabic ? this.messages_ar : this.messages_en;
    
    if (selection.includes('explanation') || selection.includes('شرح')) {
      this.handleLinkAction('websiteExplanation');
      this.showReturnAndCustomerServiceOptions();
    } else if (selection.includes('link') || selection.includes('websait')) {
      this.handleLinkAction('websiteLink');
      this.showReturnAndCustomerServiceOptions();
    } else if (selection.includes('go back') || selection.includes('العودة') || selection.includes('main')) {
      this.showMainMenuOptions();
    } else {
      this.showWebsiteUsageOptions();
    }
  }

  showBranchAddressOptions(): void {
    const isArabic = this.userInfo.language === 'ar';
    const branches = this.branchService.getBranches(this.userInfo.language);
    
    const options: QuickOption[] = branches.map(branch => ({
      text: branch.name,
      action: branch.name.toLowerCase().replace(/\s+/g, '_'),
      data: branch
    }));

    // Add go back option
    options.push({ text: isArabic ? 'العودة للقائمة الرئيسية' : 'Go Back to Main Menu', action: 'go_back_to_main' });

    const messages = isArabic ? this.messages_ar : this.messages_en;
    this.addBotMessageWithOptions(messages.branchAddresses, options);
    this.conversationState = ConversationState.BRANCH_ADDRESSES;
  }

  handleBranchAddressSelection(selection: string): void {
    const branch = this.branchService.getBranchByPartialName(selection, this.userInfo.language);
    const isArabic = this.userInfo.language === 'ar';
    
    if (selection.includes('go back') || selection.includes('العودة') || selection.includes('main')) {
      this.showMainMenuOptions();
      return;
    }
    
    if (branch) {
      const phoneText = isArabic ? 'أرقام الهاتف: ' : 'Phone Numbers: ';
      const locationText = isArabic ? 'العنوان: ' : 'Address: ';
      const mapText = isArabic ? 'رابط الخريطة: ' : 'Map Link: ';
      
      // Create message without map link (we'll open it separately)
      const message = `${branch.name}\n\n${locationText}${branch.location}\n\n${phoneText}${branch.phones.join(', ')}`;
      
      this.addBotMessage(message);
      
      // Open map link in new tab if it exists
      if (branch.mapLink) {
        this.openLinkInNewTab(branch.mapLink);
        const confirmationMessage = isArabic ? 'تم فتح رابط الخريطة في تبويب جديد' : 'Map link opened in new tab';
        this.addBotMessage(confirmationMessage);
      }
      
      this.showReturnAndCustomerServiceOptions();
    } else {
      const messages = this.userInfo.language === 'ar' ? this.messages_ar : this.messages_en;
      this.addBotMessage(isArabic ? 'عذراً، لم نتمكن من العثور على هذا الفرع. يرجى المحاولة مرة أخرى.' : 'Sorry, we could not find this branch. Please try again.');
      this.showReturnAndCustomerServiceOptions();
    }
  }

  showCustomerServiceOptions(): void {
    const isArabic = this.userInfo.language === 'ar';
    const options: QuickOption[] = [
      { text: isArabic ? 'مواعيد العمل' : 'Working Hours', action: 'working_hours' },
      { text: isArabic ? 'طلب فتح حساب' : 'Account Opening Request', action: 'account_opening' },
      { text: isArabic ? 'ارقام الحسابات' : 'Account Numbers', action: 'account_numbers' },
      { text: isArabic ? 'التحدث مع خدمة العملاء' : 'Chat with Customer Service', action: 'chat_with_customer_service' },
      { text: isArabic ? 'العودة للقائمة الرئيسية' : 'Go Back to Main Menu', action: 'go_back_to_main' }
    ];

    const messages = isArabic ? this.messages_ar : this.messages_en;
    this.addBotMessageWithOptions('Customer Service Options:', options);
    this.conversationState = ConversationState.CUSTOMER_SERVICE;
  }

  handleCustomerServiceSelection(selection: string): void {
    const isArabic = this.userInfo.language === 'ar';
    const messages = isArabic ? this.messages_ar : this.messages_en;
    
    if (selection.includes('working') || selection.includes('مواعيد')) {
      this.addBotMessage(messages.workingHours);
      this.showReturnAndCustomerServiceOptions();
    } else if (selection.includes('opening') || selection.includes('فتح')) {
      this.startAccountOpeningForm();
    } else if (selection.includes('numbers') || selection.includes('ارقام')) {
      this.displayBankAccountDetails();
    } else if (selection.includes('chat') || selection.includes('التحدث')) {
      // Start backend chat session
      this.startBackendChatSession();
    } else if (selection.includes('go back') || selection.includes('العودة') || selection.includes('main')) {
      this.showMainMenuOptions();
    } else {
      this.showCustomerServiceOptions();
    }
  }

  displayBankAccountDetails(): void {
    const isArabic = this.userInfo.language === 'ar';
    const messages = isArabic ? this.messages_ar : this.messages_en;
    
    // Start with the detailed message
    this.addBotMessage(messages.accountNumbersDetailed);
    
    // Show currency selection options
    const options: QuickOption[] = [
      { text: isArabic ? 'حسابات الجنيه المصري (EGP)' : 'Egyptian Pound (EGP) Accounts', action: 'show_egp_accounts' },
      { text: isArabic ? 'حسابات الدولار الأمريكي (USD)' : 'US Dollar (USD) Accounts', action: 'show_usd_accounts' },
      { text: isArabic ? 'العودة للقائمة السابقة' : 'Return to Previous Menu', action: 'return_to_previous' }
    ];
    
    this.addBotMessageWithOptions(messages.selectCurrencyMessage, options);
    this.conversationState = ConversationState.CURRENCY_SELECTION;
  }

  displayEGPAccounts(): void {
    const isArabic = this.userInfo.language === 'ar';
    const messages = isArabic ? this.messages_ar : this.messages_en;
    
    this.addBotMessage(messages.egpAccountsTitle);
    const egpAccounts = this.bankAccounts.filter(account => account.currency === 'EGP');
    
    egpAccounts.forEach(account => {
      const bankName = isArabic ? account.bankNameAr : account.bankName;
      const accountInfo = messages.bankAccountFormat
        .replace('{bankName}', bankName)
        .replace('{iban}', account.iban)
        .replace('{accountNumber}', account.accountNumber);
      
      this.addBotMessage(accountInfo);
    });
    
    // Show return and customer service options
    this.showReturnAndCustomerServiceOptions();
  }

  displayUSDAccounts(): void {
    const isArabic = this.userInfo.language === 'ar';
    const messages = isArabic ? this.messages_ar : this.messages_en;
    
    this.addBotMessage(messages.usdAccountsTitle);
    const usdAccounts = this.bankAccounts.filter(account => account.currency === 'USD');
    
    usdAccounts.forEach(account => {
      const bankName = isArabic ? account.bankNameAr : account.bankName;
      const accountInfo = messages.bankAccountFormat
        .replace('{bankName}', bankName)
        .replace('{iban}', account.iban)
        .replace('{accountNumber}', account.accountNumber);
      
      this.addBotMessage(accountInfo);
    });
    
    // Show return and customer service options
    this.showReturnAndCustomerServiceOptions();
  }

  handleCurrencySelection(message: string): void {
    const isArabic = this.userInfo.language === 'ar';
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('egp') || lowerMessage.includes('جنيه') || lowerMessage.includes('مصري')) {
      this.displayEGPAccounts();
    } else if (lowerMessage.includes('usd') || lowerMessage.includes('دولار') || lowerMessage.includes('أمريكي')) {
      this.displayUSDAccounts();
    } else if (lowerMessage.includes('return') || lowerMessage.includes('العودة') || lowerMessage.includes('السابقة')) {
      this.showCustomerServiceOptions();
    } else {
      // Default to showing currency selection again
      this.displayBankAccountDetails();
    }
  }

  // Account opening form handlers
  handleAccountOpeningFullName(message: string): void {
    this.accountOpeningForm.fullName = message.trim();
    const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
    this.addBotMessage(messages.accountOpeningPhone);
    this.conversationState = ConversationState.ACCOUNT_OPENING_PHONE;
  }

  handleAccountOpeningPhone(message: string): void {
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{8,}$/;
    if (phoneRegex.test(message.trim())) {
      this.accountOpeningForm.phoneNumber = message.trim();
      const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
      this.addBotMessage(messages.accountOpeningEmail);
      this.conversationState = ConversationState.ACCOUNT_OPENING_EMAIL;
    } else {
      const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
      this.addBotMessage(messages.invalidPhone);
    }
  }

  handleAccountOpeningEmail(message: string): void {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(message.trim())) {
      this.accountOpeningForm.email = message.trim();
      const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
      this.addBotMessage(messages.accountOpeningAddress);
      this.conversationState = ConversationState.ACCOUNT_OPENING_ADDRESS;
    } else {
      const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
      this.addBotMessage(messages.invalidEmail);
    }
  }

  handleAccountOpeningAddress(message: string): void {
    this.accountOpeningForm.address = message.trim();
    const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
    this.addBotMessage(messages.accountOpeningExperience);
    this.conversationState = ConversationState.ACCOUNT_OPENING_EXPERIENCE;
  }

  handleAccountOpeningExperience(message: string): void {
    const isArabic = this.userInfo.language === 'ar';
    const lowerMessage = message.toLowerCase();
    const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
    
    if (lowerMessage.includes('yes') || lowerMessage.includes('نعم') || lowerMessage.includes('y')) {
      this.accountOpeningForm.hasExperience = true;
      this.addBotMessage(messages.accountOpeningOtherCompany);
      this.conversationState = ConversationState.ACCOUNT_OPENING_OTHER_COMPANY;
    } else if (lowerMessage.includes('no') || lowerMessage.includes('لا') || lowerMessage.includes('n')) {
      this.accountOpeningForm.hasExperience = false;
      this.addBotMessage(messages.accountOpeningOtherCompany);
      this.conversationState = ConversationState.ACCOUNT_OPENING_OTHER_COMPANY;
    } else {
      // Show options for yes/no
      const options: QuickOption[] = [
        { text: messages.yes, action: 'yes_experience' },
        { text: messages.no, action: 'no_experience' }
      ];
      this.addBotMessageWithOptions(messages.accountOpeningExperience, options);
    }
  }

  handleAccountOpeningOtherCompany(message: string): void {
    const isArabic = this.userInfo.language === 'ar';
    const lowerMessage = message.toLowerCase();
    const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
    
    if (lowerMessage.includes('yes') || lowerMessage.includes('نعم') || lowerMessage.includes('y')) {
      this.accountOpeningForm.tradesWithOtherCompany = true;
      this.submitAccountOpeningRequest();
    } else if (lowerMessage.includes('no') || lowerMessage.includes('لا') || lowerMessage.includes('n')) {
      this.accountOpeningForm.tradesWithOtherCompany = false;
      this.submitAccountOpeningRequest();
    } else {
      // Show options for yes/no
      const options: QuickOption[] = [
        { text: messages.yes, action: 'yes_other_company' },
        { text: messages.no, action: 'no_other_company' }
      ];
      this.addBotMessageWithOptions(messages.accountOpeningOtherCompany, options);
    }
  }

  startAccountOpeningForm(): void {
    const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
    this.addBotMessage(messages.accountOpeningTitle);
    this.addBotMessage(messages.accountOpeningFullName);
    this.conversationState = ConversationState.ACCOUNT_OPENING_FULL_NAME;
  }

  async submitAccountOpeningRequest(): Promise<void> {
    const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
    
    try {
      const request = {
        fullName: this.accountOpeningForm.fullName,
        phoneNumber: this.accountOpeningForm.phoneNumber,
        email: this.accountOpeningForm.email,
        address: this.accountOpeningForm.address,
        hasExperience: this.accountOpeningForm.hasExperience,
        tradesWithOtherCompany: this.accountOpeningForm.tradesWithOtherCompany,
        language: this.userInfo.language
      };

      const response = await this.chatService.createAccountOpeningTicket(request);
      
      if (response.success) {
        this.addBotMessage(messages.accountOpeningSuccess);
      } else {
        this.addBotMessage(messages.accountOpeningError);
      }
      
      // Reset form and return to main menu
      this.accountOpeningForm = {
        fullName: '',
        phoneNumber: '',
        email: '',
        address: '',
        hasExperience: false,
        tradesWithOtherCompany: false
      };
      
      this.showMainMenuOptions();
    } catch (error) {
      console.error('Error submitting account opening request:', error);
      this.addBotMessage(messages.accountOpeningError);
      this.showMainMenuOptions();
    }
  }

  async startBackendChatSession(): Promise<void> {
    const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
    
    // Show connecting message
    this.addBotMessage(messages.connecting);
    
    try {
      const sessionRequest: StartChatSessionRequest = {
        name: this.userInfo.name,
        key: this.generateSessionKey(),
        phone: this.userInfo.phone,
        email: this.userInfo.email,
        language: this.userInfo.language
      };

      const response = await this.chatService.startChatSession(sessionRequest);
      
      if (response.success) {
        this.sessionStarted = true;
        
        // Show success message
        this.addBotMessage(messages.sessionStarted);
        
        // Start polling for messages from backend
        this.startMessagePolling();
        
        this.conversationState = ConversationState.BACKEND_CHAT;
        
        console.log('Backend session started successfully with ID:', response.sessionId);
      } else {
        this.addBotMessage(messages.sessionError);
        console.error('Failed to start backend session:', response.message);
        this.showMainMenuOptions();
      }
    } catch (error) {
      console.error('Error starting backend session:', error);
      this.addBotMessage(messages.sessionError);
      this.sessionStarted = false;
      this.showMainMenuOptions();
    }
  }

  async handleBackendConversation(message: string): Promise<void> {
    if (!this.sessionStarted) {
      const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
      this.addBotMessage(messages.sessionError);
      return;
    }

    try {
      const response = await this.chatService.sendMessage(message);
      if (!response.success) {
        const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
        this.addBotMessage(messages.messageError);
      }
      // Note: Responses will be handled by the message polling system
    } catch (error) {
      console.error('Error sending message:', error);
      const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
      this.addBotMessage(messages.messageError);
    }
  }

  private startMessagePolling(): void {
    // Poll for new messages every 2 seconds
    this.messagePollingSubscription = interval(2000).subscribe(async () => {
      try {
        const messages = await this.chatService.getSessionMessages();
        
        // Only process if we have new messages
        if (messages.length > this.lastMessageCount) {
          const newMessages = messages.slice(this.lastMessageCount);
          
          newMessages.forEach(message => {
            if (!message.isUser) {
              // Only add bot messages from backend (user messages are already added locally)
              this.addBotMessage(message.text);
            }
          });
          
          this.lastMessageCount = messages.length;
        }
      } catch (error) {
        console.error('Error polling messages:', error);
      }
    });
  }

  private generateSessionKey(): string {
    return `${this.userInfo.name}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Helper method to extract URL from a message
  private extractUrl(message: string): string | null {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const match = message.match(urlRegex);
    return match ? match[0] : null;
  }

  // Helper method to open link in new tab
  private openLinkInNewTab(url: string): void {
    window.open(url, '_blank');
  }

  // Helper method to handle link actions
  private handleLinkAction(messageKey: string): void {
    const messages = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
    const message = messages[messageKey as keyof typeof messages] as string;
    const url = this.extractUrl(message);
    
    if (url) {
      this.openLinkInNewTab(url);
      // Add a brief confirmation message
      const isArabic = this.userInfo.language === 'ar';
      const confirmationMessage = isArabic ? 'تم فتح الرابط في تبويب جديد' : 'Link opened in new tab';
      this.addBotMessage(confirmationMessage);
    } else {
      // Fallback to showing the message if no URL found
      this.addBotMessage(message);
    }
  }

  handleGeneralConversation(message: string): void {
    const lowerMessage = message.toLowerCase();
    const isArabic = this.userInfo.language === 'ar';
    let response = '';

    if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('مرحبا') || lowerMessage.includes('أهلا')) {
      response = isArabic 
        ? `مرحباً ${this.userInfo.name}! كيف يمكنني مساعدتك اليوم؟ 😊`
        : `Hello ${this.userInfo.name}! How can I help you today? 😊`;
    } else if (lowerMessage.includes('help') || lowerMessage.includes('مساعدة')) {
      const messages = isArabic ? this.messages_ar : this.messages_en;
      response = messages.helpMessage;
    } else if (lowerMessage.includes('service') || lowerMessage.includes('خدمة') || lowerMessage.includes('خدمات')) {
      response = isArabic
        ? 'نحن نقدم مجموعة واسعة من الخدمات المهنية. هل تود معرفة المزيد عن مجال معين؟'
        : 'We offer a wide range of professional services. Would you like me to tell you more about any specific area?';
    } else if (lowerMessage.includes('contact') || lowerMessage.includes('اتصال') || lowerMessage.includes('تواصل')) {
      response = isArabic
        ? 'يمكنك التواصل معنا من خلال صفحة الاتصال، أو يمكنني مساعدتك هنا مباشرة! ما المعلومات المحددة التي تحتاجها؟'
        : 'You can reach us through our contact page, or I can help you right here! What specific information do you need?';
    } else if (lowerMessage.includes('price') || lowerMessage.includes('cost') || lowerMessage.includes('سعر') || lowerMessage.includes('تكلفة')) {
      response = isArabic
        ? 'تختلف أسعارنا حسب احتياجاتك المحددة. هل تود أن أوصلك بفريق المبيعات للحصول على عرض سعر مخصص؟'
        : 'Our pricing varies depending on your specific needs. Would you like me to connect you with our sales team for a personalized quote?';
    } else {
      const responses_en = [
        `That's an interesting question, ${this.userInfo.name}! Let me help you with that. Could you provide a bit more detail?`,
        `I'd be happy to help you with that, ${this.userInfo.name}! Can you tell me more about what you're looking for?`,
        `Great question, ${this.userInfo.name}! I'm here to assist you. What specific information would be most helpful?`
      ];
      
      const responses_ar = [
        `هذا سؤال مثير للاهتمام، ${this.userInfo.name}! دعني أساعدك في ذلك. هل يمكنك تقديم المزيد من التفاصيل؟`,
        `سأكون سعيداً لمساعدتك في ذلك، ${this.userInfo.name}! هل يمكنك إخباري أكثر عما تبحث عنه؟`,
        `سؤال رائع، ${this.userInfo.name}! أنا هنا لمساعدتك. ما المعلومات المحددة التي ستكون أكثر فائدة؟`
      ];
      
      const responseArray = isArabic ? responses_ar : responses_en;
      response = responseArray[Math.floor(Math.random() * responseArray.length)];
    }

    this.addBotMessage(response);
  }

  showLanguageButtons(): void {
    // This will be handled in the template with buttons
  }

  addUserMessage(text: string): void {
    const message: ChatMessage = {
      text,
      isUser: true,
      timestamp: new Date(),
      id: this.generateId()
    };
    this.messages.push(message);
    // Always scroll to bottom for user messages
    setTimeout(() => this.scrollToBottom(), 100);
  }

  addBotMessage(text: string): void {
    const message: ChatMessage = {
      text,
      isUser: false,
      timestamp: new Date(),
      id: this.generateId()
    };
    this.messages.push(message);
    // Only scroll if user hasn't manually scrolled up
    setTimeout(() => {
      if (!this.userHasScrolledUp) {
        this.scrollToBottom();
      }
    }, 100);
  }

  addBotMessageWithOptions(text: string, options: QuickOption[]): void {
    const message: ChatMessage = {
      text,
      isUser: false,
      timestamp: new Date(),
      id: this.generateId(),
      options
    };
    this.messages.push(message);
    // Only scroll if user hasn't manually scrolled up
    setTimeout(() => {
      if (!this.userHasScrolledUp) {
        this.scrollToBottom();
      }
    }, 100);
  }

  selectOption(option: QuickOption): void {
    this.addUserMessage(option.text);
    
    // If the option has branch data, use it directly
    if (option.data && option.data.name) {
      const branch = option.data as Branch;
      const isArabic = this.userInfo.language === 'ar';
      const phoneText = isArabic ? 'أرقام الهاتف: ' : 'Phone Numbers: ';
      const locationText = isArabic ? 'العنوان: ' : 'Address: ';
      
      // Create message without map link (we'll open it separately)
      const message = `${branch.name}\n\n${locationText}${branch.location}\n\n${phoneText}${branch.phones.join(', ')}`;
      
      this.addBotMessage(message);
      
      // Open map link in new tab if it exists
      if (branch.mapLink) {
        this.openLinkInNewTab(branch.mapLink);
        const confirmationMessage = isArabic ? 'تم فتح رابط الخريطة في تبويب جديد' : 'Map link opened in new tab';
        this.addBotMessage(confirmationMessage);
      }
      
      this.showReturnAndCustomerServiceOptions();
    } else {
      // Handle other options based on action
      const isArabic = this.userInfo.language === 'ar';
      const messages = isArabic ? this.messages_ar : this.messages_en;
      
      switch (option.action) {
        // Main menu actions
        case 'about_company':
          this.addBotMessage(messages.aboutCompany);
          this.showReturnAndCustomerServiceOptions();
          break;
        case 'services':
          this.showServicesMenu();
          return; // Don't show main menu, show services menu instead
        case 'application_usage':
          this.showApplicationUsageOptions();
          return; // Don't show main menu, show application options instead
        case 'website_usage':
          this.showWebsiteUsageOptions();
          return; // Don't show main menu, show website options instead
        case 'branch_addresses':
          this.showBranchAddressOptions();
          return; // Don't show main menu, show branch options instead
        case 'customer_service':
          this.showCustomerServiceOptions();
          return; // Don't show main menu, show customer service options instead
        case 'stock_prices':
          this.showStockPricesOptions();
          return; // Don't show main menu, show stock prices options instead
        
        // Go back action
        case 'go_back_to_main':
          this.showMainMenuOptions();
          return;
        
        // Services menu actions
        case 'account_manager':
          this.addBotMessage(messages.accountManager);
          this.showReturnAndCustomerServiceOptions();
          break;
        case 'mobile_app':
          this.addBotMessage(messages.mobileApp);
          this.showReturnAndCustomerServiceOptions();
          break;
        case 'e_trade':
          this.addBotMessage(messages.eTrade);
          this.showReturnAndCustomerServiceOptions();
          break;
        case 'margin':
          this.addBotMessage(messages.margin);
          this.showReturnAndCustomerServiceOptions();
          break;
        case 'competitive_commissions':
          this.addBotMessage(messages.competitiveCommissions);
          this.showReturnAndCustomerServiceOptions();
          break;
        case 'financial_group':
          this.startStockRecommendationsFlow();
          return;
        case 'stock_recommendations':
          this.startStockRecommendationsFlow();
          return;
        
        // Application usage actions
        case 'app_explanation':
          this.handleLinkAction('appExplanation');
          this.showReturnAndCustomerServiceOptions();
          break;
        case 'ios_download':
          this.handleLinkAction('iosDownload');
          this.showReturnAndCustomerServiceOptions();
          break;
        case 'android_download':
          this.handleLinkAction('androidDownload');
          this.showReturnAndCustomerServiceOptions();
          break;
        
        // Website usage actions
        case 'website_explanation':
          this.handleLinkAction('websiteExplanation');
          this.showReturnAndCustomerServiceOptions();
          break;
        case 'website_link':
          this.handleLinkAction('websiteLink');
          this.showReturnAndCustomerServiceOptions();
          break;
        
        // Customer service actions
        case 'working_hours':
          this.addBotMessage(messages.workingHours);
          this.showReturnAndCustomerServiceOptions();
          break;
        case 'account_opening':
          this.startAccountOpeningForm();
          return;
        case 'account_numbers':
          this.displayBankAccountDetails();
          return;
        case 'show_egp_accounts':
          this.displayEGPAccounts();
          return;
        case 'show_usd_accounts':
          this.displayUSDAccounts();
          return;
        case 'chat_with_customer_service':
          this.startBackendChatSession();
          return; // Don't show main menu for this one
        case 'stock_prices':
          this.showStockPricesOptions();
          return;
        case 'select_stock':
          if (option.data) {
            this.displayStockInfo(option.data);
          }
          return;
        case 'search_another_stock':
          this.showStockSearchPrompt();
          return;
        case 'return_to_previous':
          this.showMainMenuOptions();
          return;
        case 'contact_customer_service':
          this.showCustomerServiceOptions();
          return;
        case 'yes_experience':
          this.accountOpeningForm.hasExperience = true;
          const messagesExp = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
          this.addBotMessage(messagesExp.accountOpeningOtherCompany);
          this.conversationState = ConversationState.ACCOUNT_OPENING_OTHER_COMPANY;
          return;
        case 'no_experience':
          this.accountOpeningForm.hasExperience = false;
          const messagesExp2 = this.userInfo.language === 'en' ? this.messages_en : this.messages_ar;
          this.addBotMessage(messagesExp2.accountOpeningOtherCompany);
          this.conversationState = ConversationState.ACCOUNT_OPENING_OTHER_COMPANY;
          return;
        case 'yes_other_company':
          this.accountOpeningForm.tradesWithOtherCompany = true;
          this.submitAccountOpeningRequest();
          return;
        case 'no_other_company':
          this.accountOpeningForm.tradesWithOtherCompany = false;
          this.submitAccountOpeningRequest();
          return;
        default:
          // Fall back to text-based processing
          this.processUserInput(option.text);
          return;
      }
    }
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  onScroll(): void {
    // Simple scroll tracking - only mark as scrolled up if user is clearly not at bottom
    try {
      if (this.messagesContainer) {
        const element = this.messagesContainer.nativeElement;
        const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 50;
        
        // Only mark as scrolled up if user is clearly not at the bottom
        if (!isAtBottom && element.scrollTop > 100) {
          this.userHasScrolledUp = true;
        } else if (isAtBottom) {
          this.userHasScrolledUp = false;
        }
      }
    } catch (err) {
      console.error('Error tracking scroll position:', err);
    }
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        // Use setTimeout to ensure DOM is updated
        setTimeout(() => {
          const element = this.messagesContainer.nativeElement;
          element.scrollTop = element.scrollHeight;
        }, 100);
      }
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  trackByMessageId(index: number, message: ChatMessage): string {
    return message.id;
  }

  // Getter to check if we should show language buttons
  get shouldShowLanguageButtons(): boolean {
    return this.conversationState === ConversationState.LANGUAGE_SELECTION;
  }
}
